import React from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
} from "react-router-dom";
import "./App.scss";
import { ConfigProvider } from "antd";
import ScenarioPage from "./pages/Scenario/ScenarioPage";
import NewsletterPage from "./pages/NewsletterPage";
import CommonLayout from "./components/Layout";
import AdminLayout from "./components/Layout/AdminLayout";
import ChatPage from "./pages/chat";
import KnowledgeBase from "./pages/KnowledgeBasePage";
import LoginPage from "./pages/Login/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import { isLoggedIn, isAdmin } from "./services/ApiActions/authAct"; // 💡 IMPORT YOUR ROLE CHECK HERE
import RecentUpdatesPage from "./pages/RecentUpdatesPage";
import ResearchReportPage from "./pages/ResearchReportPage";
import LearnPage from "./pages/LearnPage";
import BestCallsPage from "./pages/BestCalls/BestCallsPage";
import BestCallDetailsPage from "./pages/BestCalls/BestCallDetailsPage";
import LMSPage from "./pages/LMSPage";
import LMSCoursePage from "./pages/LMSCoursePage";
import LMSVideoPage from "./pages/LMSVideoPage";
import ProfileModal from "./components/Profile/ProfileModal";
import ResetPassword from "./components/Profile/ResetPassword";
import AdminPage from "./pages/AdminPage";
import PeoplePage from "./pages/PeoplePage";
import AdminNotification from "./pages/AdminNotification";
import AdminAssessmentPage from "./pages/AdminAssessmentPage";
import AssessmentPage from "./pages/AssessmentPage";
import NotificationPage from "./pages/NotificationPage";
import PasswordChangeSuccessPage from "./pages/PasswordChangeSuccess";
import AchievementsPage from "./pages/AchievementsPage";
import MarketingPage from "./pages/MarketingPage";

import MCQReviewPage from "./pages/Review/MCQReviewPage";
import NoModulesPage from "./pages/Review/NoModule";
import WhatsTrendingPage from "./pages/WhatsTrendingPage";
import InformedInvestorPage from "./pages/InformedInvestorPage";
import TrainingAssessmentUserListPage from "./pages/admin/TrainingAssessmentUserListPage";
import TrainingAssessmentFormPage from "./pages/TrainingAssessmentFormPage";
import AssesmentPage from "./pages/AssesmentPage";
import TrainingAssessmentPage from "./pages/admin/TrainingAssessmentPage";
import TrainingAssessmentReviewPage from "./pages/admin/TrainingAssessmentReviewPage";
import DashboardPage from "./pages/Dashboard/DashboardPage";
import LmsConsumptionDashboard from "./pages/LmsConsumptionDashboard/LmsConsumptionDashboardPage";

type AuthRouteProps = {
  component: React.ComponentType;
};

// 1. Existing Standard Authentication Guard
const AuthRoute = ({ component: Component }: AuthRouteProps) => {
  const location = useLocation();
  if (!isLoggedIn()) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  return <Component />;
};

// 2. CRITICAL VAPT FIX: New Admin Authorization Guard
const AdminRoute = ({ component: Component }: AuthRouteProps) => {
  const location = useLocation();
  
  // First, ensure they are logged in
  if (!isLoggedIn()) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  // Second, validate server-side role permission
  if (!isAdmin()) {
    // If they aren't an admin, kick them out to a safe page (e.g., home/chat)
    return <Navigate to="/ask-me-anything" replace />;
  }

  return <Component />;
};

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Navigate to="/login" />, 
  },
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/password-change-success",
    element: <PasswordChangeSuccessPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/",
    element: <AdminLayout />,
    children: [
      // 3. CRITICAL VAPT FIX: Upgraded these routes from AuthRoute to AdminRoute
      { path: "/admin", element: <AdminRoute component={AdminPage} /> },
      {
        path: "/admin-training-assessment",
        element: <AdminRoute component={TrainingAssessmentPage} />,
      },
      {
        path: "/admin-training-assessment-users/:assessmentId",
        element: <AdminRoute component={TrainingAssessmentUserListPage} />,
      },
      {
        path: "/admin-training-assessment-review",
        element: <AdminRoute component={TrainingAssessmentReviewPage} />,
      },
      {
        path: "/people/:userId/:selectedTab",
        element: <AdminRoute component={PeoplePage} />,
      },
      {
        path: "/admin-assessment",
        element: <AdminRoute component={AdminAssessmentPage} />,
      },
      {
        path: "/admin-notification",
        element: <AdminRoute component={AdminNotification} />,
      },
      {
        path: "/admin-quiz-review",
        element: <AdminRoute component={MCQReviewPage} />,
      },
      { path: "/no-module", element: <AuthRoute component={NoModulesPage} /> },
    ],
  },
  {
    path: "/",
    element: <CommonLayout fullHeight={true} />,
    children: [
      { path: "/no-module", element: <AuthRoute component={NoModulesPage} /> },
      {
        path: "/ask-me-anything",
        element: <AuthRoute component={ChatPage} />,
      },
      {
        path: "/ask-me-anything/:chatId",
        element: <AuthRoute component={ChatPage} />,
      },
      {
        path: "/scenarios/:scenarioId/:contentId",
        element: <AuthRoute component={ScenarioPage} />,
      },
    ],
  },
  {
    path: "/",
    element: <CommonLayout />,
    children: [
      {
        path: "/dashboard",
        element: <AuthRoute component={DashboardPage} />,
      },
      {
        path: "/lms-consumption-dashboard",
        element: <AuthRoute component={LmsConsumptionDashboard} />,
      },
      {
        path: "/knowledge-base/:knowledgeBaseCategory",
        element: <AuthRoute component={KnowledgeBase} />,
      },
      {
        path: "/research-reports/:reportName",
        element: <AuthRoute component={ResearchReportPage} />,
      },
      {
        path: "/newsletter",
        element: <AuthRoute component={NewsletterPage} />,
      },
      {
        path: "/informed-investor-rr/:updateType",
        element: <AuthRoute component={InformedInvestorPage} />,
      },
      {
        path: "/learn",
        element: <AuthRoute component={LearnPage} />,
      },
      {
        path: "/best-calls",
        element: <AuthRoute component={BestCallsPage} />,
      },
      {
        path: "/best-calls/:departmentName",
        element: <AuthRoute component={BestCallDetailsPage} />,
      },
      {
        path: "/lms",
        element: <AuthRoute component={LMSPage} />,
      },
      {
        path: "/lms/course/:courseId",
        element: <AuthRoute component={LMSCoursePage} />,
      },
      {
        path: "/lms/course/:courseId/assesment/:assesmentId",
        element: <AuthRoute component={AssesmentPage} />,
      },
      {
        path: "/lms/course/:courseId/:moduleId/chapter",
        element: <AuthRoute component={LMSVideoPage} />,
      },
      {
        path: "/lms/course/:courseId/:moduleId/chapter/:chapterId",
        element: <AuthRoute component={LMSVideoPage} />,
      },
      {
        path: "/lms/training-assessment/:courseId",
        element: <AuthRoute component={AssessmentPage} />,
      },
      {
        path: "/lms/start-training-assessment/:assesmentId",
        element: <AuthRoute component={TrainingAssessmentFormPage} />,
      },
      {
        path: "/reset-password",
        element: <AuthRoute component={ResetPassword} />,
      },
      {
        path: "/profile-modal",
        element: <AuthRoute component={ProfileModal} />,
      },
      {
        path: "/user-notification",
        element: <AuthRoute component={NotificationPage} />,
      },
      {
        path: "/achievements",
        element: <AuthRoute component={AchievementsPage} />,
      },
      {
        path: "/whats-trending/:reportName",
        element: <AuthRoute component={WhatsTrendingPage} />,
      },
      {
        path: "/marketing/:subTab",
        element: <AuthRoute component={MarketingPage} />,
      },
    ],
  },
]);

function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#001e5a",
          fontFamily: '"Open Sans", sans-serif',
          fontSize: 14,
        },
        components: {
          Table: {
            cellPaddingInline: 5,
            cellPaddingBlock: 10,
          },
          Layout: {
            bodyBg: "transparent",
          },
          Avatar: {
            textFontSize: 16,
            textFontSizeLG: 16,
            textFontSizeSM: 16,
          },
          Menu: {
            itemSelectedBg: "#001e5a",
            itemSelectedColor: "#fff",
            subMenuItemBg: "transparent",
            horizontalItemSelectedBg: "green",
          },
          DatePicker: {
            activeBg: "#F0E2D5",
            activeBorderColor: "#9B6F49",
            cellActiveWithRangeBg: "#F0E2D5",
            cellHoverBg: "#9B6F49",
            hoverBorderColor: "#9B6F49",
            hoverBg: "#F0E2D5",
          },
          Progress: {},
          Collapse: {
            contentPadding: "4px 36px !important",
          },
        },
      }}
    >
      <RouterProvider router={router} />
    </ConfigProvider>
  );
}

export default App;